<script setup lang="ts">
const route = useRoute();
const slug = route.params.slug;
const config = useRuntimeConfig();
const baseURL = config.public.baseUrl;
const { data, error } = await useFetch(
  `${baseURL}/content/dedicated-team/${slug}-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

useHead({
  title: data.value?.meta_tags?.title || "",
  meta: data.value?.meta_tags?.meta || [],
});

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

const requirementText = ref([
  data.value?.header?.exp,
  data.value?.header.qualification,
]);

const technologies = ref(data.value?.header.skill.split(", "));
const listItem = ref([
  data.value?.apply?.block_list_item_1,
  data.value?.apply?.block_list_item_2,
  data.value?.apply?.block_list_item_3,
  data.value?.apply?.block_list_item_4,
]);

const adminCards = data.value?.staffing_detail_why?.staffing_icon_card_content;

const text = `Ready to hire us? <span class=" underline cursor-pointer" @click="openModal" Click here</span> and let’s get cracking!`;
</script>

<template>
  <section class="max-w-screen-xl mx-auto xl:px-4 px-6 relative sm:mt-10 z-10">
    <div
      class="flex lg:items-start items-center justify-center xl:justify-between max-lg:flex-col-reverse"
    >
      <div class="flex flex-col items-start lg:max-w-[45%] w-full">
        <h1 class="font-bold">
          <WrGradientText append-class="pb-0 leading-[1.3]">
            {{ data?.header.title }}</WrGradientText
          >
        </h1>

        <p
          class="md:text-lg tracking-[0.5px] !leading-[1.8] mt-7 w-full max-sm:mt-2"
        >
          {{ data.header.subtitle }}
        </p>
        <div
          class="flex items-center max-sm:flex-col max-sm:items-start max-sm:gap-1 justify-between w-full mt-7"
        >
          <p class="text-sm leading-6" v-for="text in requirementText">
            {{ text }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2 items-center mt-5">
          <div
            v-for="tech in technologies"
            class="dark:block dark:bg-[linear-gradient(91.95deg,#51277B_4.5%,#8F5BC4_96.91%)] shadow-[1px_2px_5px_0px_#00000026] rounded-full max-w-max"
          >
            <Tag
              unstyled
              class="dark:bg-[linear-gradient(92.4deg,#270945_6.22%,#4C127F_93.78%)] dark:text-white bg-[#eef4fb] text-[#276bb9] px-2 py-1 rounded-full text-[11px] font-normal"
            >
              {{ tech }}</Tag
            >
          </div>
        </div>

        <WrButton container-class="mt-4" type="button" @click="openModal">
          {{ data.header.button_label }}
        </WrButton>
      </div>
      <div class="w-full lg:max-w-[45%]">
        <NuxtImg
          :src="data.header.image"
          loading="eager"
          width="467"
          height="479"
          class="max-lg:mx-auto"
          format="webp"
          fetch-priority="high"
          alt="Dedicated Team Image"
          preload
        />
      </div>
    </div>
  </section>

  <section class="mt-10 md:mt-20 lg:py-8 pb-20 relative z-10">
    <div class="max-w-screen-xl mx-auto xl:px-4 px-10 max-lg:px-4">
      <h2 class="font-bold lg:text-center relative">
        <WrGradientText append-class="leading-[1.3]">
          {{ data.apply?.heading }}</WrGradientText
        >
      </h2>
      <p class="text-lg leading-[1.125] font-semibold lg:text-center">
        {{ data.apply?.subheading }}
      </p>
      <p class="text-base lg:text-center mt-5 lg:px-12 max-lg:px-0">
        {{ data.apply?.description }}
      </p>

      <div
        class="flex lg:items-start justify-between w-full relative z-10 max-lg:flex-col max-xl: gap-10 mt-16"
      >
        <div class="flex flex-col items-start w-full lg:max-w-[35%]">
          <h2 class="lg:text-[29px] text-[31px] mt-5 font-bold">
            <WrGradientText append-class="pb-0 leading-[1.3]">
              {{ data.apply?.block_title }}</WrGradientText
            >
          </h2>
          <ul class="flex flex-col w-full items-start gap-y-3 mt-6">
            <li
              v-for="item in data?.apply.block_list_items"
              class="bg-[url('/images/right-single.webp')] bg-no-repeat bg-[position:0_8px] m-0 pl-[22px] w-full text-base"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div class="w-full h-full lg:max-w-[75%] relative z-10 md:ml-16">
          <span
            class="absolute rounded-full dark:hidden bg-[#e9effc] block size-[288px] -right-[120px] -bottom-20"
          ></span>
          <div
            class="absolute -left-[60px] max-md:left-0 max-md:-bottom-[50px] bottom-[50px] bg-[#fff] dark:bg-[image:var(--bg-official-partner)] rounded-md z-10"
          >
            <div
              class="border-solid border-[#5f4ca4] dark:border-white border-l-[4px] border-t-0 border-r-0 border-b-0 rounded-[4px] py-[1.25em] px-[1.5em]"
            >
              <div class="flex items-start text-sm">
                <div class="flex-[1_1_auto]">
                  <h3
                    class="text-[1rem] font-semibold leading-[1.125] relative"
                  >
                    Let’s start together
                  </h3>
                  <p class="">
                    Ready to hire us?
                    <span class="underline cursor-pointer" @click="openModal"
                      >Click here</span
                    >
                    and let’s get cracking!
                  </p>
                </div>
              </div>
            </div>
          </div>
          <NuxtImg
            src="/images/dedicated-team-right.webp"
            height="394"
            width="662"
            loading="lazy"
            class="relative"
            format="webp"
            alt="Dedicated Team Image"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="relative z-[1] max-w-screen-xl mx-auto px-4 mt-10">
    <h2 class="font-bold md:text-center">
      <WrGradientText append-class="pb-0 leading-[1.3]">
        {{ data.staffing_detail_why?.heading }}</WrGradientText
      >
    </h2>
    <p class="mt-5 !leading-[1.8] md:text-lg md:text-center md:px-10">
      {{ data.staffing_detail_why?.subheading }}
    </p>
    <div
      class="grid sm:grid-cols-2 lg:grid-cols-3 mt-10 gap-4 md:gap-6 max-sm:justify-center max-sm:items-center"
    >
      <CardFunctionality
        v-for="(item, i) in adminCards"
        :key="i"
        :title="item.heading"
        :description="item.subheading"
        class="text-center"
      />
    </div>
  </section>

  <section class="relative z-10 py-10 lg:pb-28 overflow-visible order-9">
    <div class="max-w-screen-lg mx-auto px-4 w-full relative">
      <div
        class="hidden dark:block opacity-30 size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] -right-80"
      ></div>
      <WrSelectedProject />
    </div>
  </section>

  <WrBannerCTA
    :heading="`Ready to Get Started?`"
    :paragraph-text="`Affordable pricing, industry specific development, latest technology integration, scalability is a start at WebReinvent! Our refined talent acquisition process along with world-class training helps you pair with expert developers perfect fit to your project.`"
    :button-text="`Get Started`"
  />

  <section
    class="relative pt-10 lg:pt-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
  >
    <h2 class="text-center mb-11">
      <WrGradientText
        text="Trusted for excellence and efficiency"
        append-class="text-3xl lg:text-[48px]"
      />
      <p
        class="leading-[1.8] text-lg text-[var(--foreground)] font-anvtgrade-book"
      >
        Webreinvent is trusted by leading organisations to enhance their
        development and operational efficiency.
      </p>
    </h2>
    <!-- Carousel Section -->
    <WrTestimonialCardCarousel />
    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
      >View All</WrButton
    >
  </section>
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>
